import React, { Component } from 'react';

import PropTypes from 'prop-types';
import { compose } from 'redux';

import { reloadCompany } from 'common/actions/company';
import AJAX from 'common/AJAX';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { OpenModalContext } from 'common/containers/ModalContainer';
import { ShowToastContext, ToastTypes } from 'common/containers/ToastContainer';
import connect from 'common/core/connect';
import Helmet from 'common/helmets/Helmet';
import LazyLoadedImage from 'common/LazyLoadedImage';
import ConfirmModal from 'common/modals/ConfirmModal';
import { buildAzureAuthURL } from 'common/OAuthAzure';
import withAccessControl from 'common/routing/withAccessControl';
import Spinner from 'common/Spinner';
import AdminFeatureBlock from 'common/subdomain/admin/AdminFeatureBlock';
import Alert, { AlertTypes } from 'common/ui/Alert';
import ButtonV2 from 'common/ui/ButtonV2';
import SwitchV2 from 'common/ui/SwitchV2';
import { H3, P } from 'common/ui/Text';
import { RoutePermissions, testEveryPermission } from 'common/util/permissions';
import withContexts from 'common/util/withContexts';

import 'css/components/subdomain/admin/_AdminAzureSettings.scss';

import AdminSettingsHeader from './AdminSettings/AdminSettingsHeader';

import AzureLogoIcon from 'img/azure-logo-icon.png';

const RedirectURI = 'https://canny.io/azure-redirect';

class AdminAzureSettings extends Component {
  static propTypes = {
    company: PropTypes.shape({
      _id: PropTypes.string,
      subdomain: PropTypes.string,
    }),
    location: PropTypes.shape({
      query: PropTypes.shape({
        code: PropTypes.string,
      }),
    }),
    openModal: PropTypes.func,
    router: PropTypes.object,
    viewer: PropTypes.object,
  };

  state = {
    code: this.props.location.query.code,
    registering: false,
    saving: false,
    forcingMemberAuth: this.props.company.forcedMemberAuthMechanism === 'azure',
    revokeAdminAccess: this.props.company.azure?.revokeAdminAccess,
    revokeBoardAccess: this.props.company.azure?.revokeBoardAccess,
    hasChangedMemberAuth: false,
  };

  constructor(props, context) {
    super(props, context);
  }

  componentDidMount() {
    const { location, router, showToast } = this.props;

    const { code } = this.state;
    if (!code) {
      return;
    }

    router.replace({
      pathname: location.pathname,
    });

    this.setState({
      registering: true,
    });

    AJAX.post(
      '/api/azure/register',
      {
        code,
      },
      (response) => {
        if (response !== 'success') {
          showToast('Something went wrong, please try again later', ToastTypes.error);
          this.setState({
            registering: false,
          });
          return;
        }

        const { reloadCompany } = this.props;
        reloadCompany().then(() => {
          this.setState({
            registering: false,
          });
        });
      },
    );
  }

  getAuthURL = () => {
    const {
      company: { subdomain },
    } = this.props;

    return buildAzureAuthURL({
      state: { subdomain },
      redirectURI: RedirectURI,
      scope:
        'offline_access user.read.all group.read.all organization.read.all directory.read.all openid email profile',
    });
  };

  onDisconnect = () => {
    const { openModal, showToast } = this.props;
    openModal(ConfirmModal, {
      message: `Are you sure you'd like to disconnect your Microsoft Entra ID account?`,
      onConfirm: () => {
        AJAX.post('/api/azure/disconnect', {}, (response) => {
          if (response !== 'success') {
            showToast(
              'Something went wrong, please contact our team for support.',
              ToastTypes.error,
            );
          } else {
            const { reloadCompany } = this.props;
            reloadCompany();
          }
        });
      },
    });
  };

  onToggleForcingMemberAuth = () => {
    this.setState((state) => ({
      forcingMemberAuth: !state.forcingMemberAuth,
      hasChangedMemberAuth: true,
    }));
  };

  onToggleBoardAccess = () => {
    this.setState((state) => ({
      revokeBoardAccess: !state.revokeBoardAccess,
      hasChangedOffboardSettings: true,
    }));
  };

  onToggleAdminAccess = () => {
    this.setState((state) => ({
      revokeAdminAccess: !state.revokeAdminAccess,
      hasChangedOffboardSettings: true,
    }));
  };

  onSave = () => {
    const {
      saving,
      hasChangedOffboardSettings,
      hasChangedMemberAuth,
      forcingMemberAuth,
      revokeAdminAccess,
      revokeBoardAccess,
    } = this.state;

    const { showToast } = this.props;
    if (saving) {
      return;
    }

    this.setState({
      saving: true,
    });

    const promises = [];

    // Save offboard settings if changed
    if (hasChangedOffboardSettings) {
      promises.push(
        new Promise((resolve, reject) => {
          AJAX.post(
            '/api/azure/updateOffboardSettings',
            {
              revokeAdminAccess,
              revokeBoardAccess,
            },
            (response) => {
              if (response === 'success') {
                resolve();
              } else {
                reject(new Error('Failed to save offboard settings'));
              }
            },
          );
        }),
      );
    }

    // Save force auth settings if changed
    if (hasChangedMemberAuth) {
      promises.push(
        new Promise((resolve, reject) => {
          AJAX.post(
            '/api/company/changeSettings',
            { forcedMemberAuthMechanism: forcingMemberAuth ? 'azure' : 'none' },
            (response) => {
              if (response === 'success') {
                resolve();
              } else {
                reject(new Error('Failed to save force auth settings'));
              }
            },
          );
        }),
      );
    }

    // Wait for all promises to resolve
    Promise.all(promises)
      .then(() => {
        const { reloadCompany } = this.props;
        return reloadCompany();
      })
      .then(() => {
        this.setState({
          hasChangedOffboardSettings: false,
          hasChangedMemberAuth: false,
          saving: false,
        });
      })
      .catch((error) => {
        showToast('Something went wrong, please try again later', ToastTypes.error);
        this.setState({
          saving: false,
        });
      });
  };

  renderConnected() {
    const { registering } = this.state;
    const { azure } = this.props.company;
    if (!azure) {
      return null;
    }

    const authURL = this.getAuthURL();
    return (
      <>
        {azure.lostAccess ? (
          <Alert type={Alert.AlertTypes.Danger}>
            <Alert.Heading>Lost access</Alert.Heading>
            <Alert.Content>
              Your Microsoft Entra ID integration has lost access to Canny. Please reinstall
              the&nbsp;integration.
            </Alert.Content>
            <Alert.CTA>
              <div className="buttonContainer">
                <ButtonV2 asChild size="medium" loading={registering}>
                  <a
                    href={authURL}
                    className="buttonContainer"
                    target="_blank"
                    rel="noreferrer noopener nofollow">
                    Reinstall Microsoft Entra ID
                  </a>
                </ButtonV2>
                <ButtonV2
                  onClick={this.onDisconnect}
                  size="medium"
                  color="error"
                  variant="outlined">
                  Uninstall
                </ButtonV2>
              </div>
            </Alert.CTA>
          </Alert>
        ) : (
          <Alert
            type={AlertTypes.Confirmation}
            headingText="Microsoft Entra ID connected"
            subText={`Your Microsoft Entra ID account (${azure.organizationName}) is connected.`}
            button={
              <ButtonV2 size="medium" onClick={this.onDisconnect} color="error" variant="outlined">
                Uninstall
              </ButtonV2>
            }
          />
        )}
      </>
    );
  }

  renderForceAuthSection() {
    const { forcingMemberAuth } = this.state;
    return (
      <>
        <div className="settingsDetails">
          <H3 variant="headingMd" className="subheading">
            Enforce Microsoft Entra ID login
          </H3>
          <div className="subheading">
            <P className="detailsBody">
              Users are required to log in with Microsoft Entra ID to access Canny admin
              functionality.
            </P>
            <SwitchV2
              size="medium"
              checked={forcingMemberAuth}
              onChange={this.onToggleForcingMemberAuth}
            />
          </div>
        </div>
      </>
    );
  }

  renderOffboardSection() {
    return (
      <div className="offboard">
        <H3 variant="headingMd" className="subheading">
          Automatically revoke access from offboarded&nbsp;employees
        </H3>
        {this.renderOffboardSettings()}
      </div>
    );
  }

  renderOffboardSettings() {
    const {
      company: { azure },
    } = this.props;

    const { revokeAdminAccess, revokeBoardAccess } = this.state;
    if (!azure) {
      return null;
    }

    return (
      <div className="settingsDetails">
        <div className="subheading">
          <P className="detailsBody">Revoke admin account</P>
          <SwitchV2 size="medium" checked={revokeAdminAccess} onChange={this.onToggleAdminAccess} />
        </div>
        <div className="subheading">
          <P className="detailsBody">Revoke private board accesss</P>
          <SwitchV2 size="medium" checked={revokeBoardAccess} onChange={this.onToggleBoardAccess} />
        </div>
      </div>
    );
  }

  renderIntegrateButton() {
    const {
      company: { integrations },
    } = this.props;
    if (!integrations?.azureAD) {
      return (
        <AdminFeatureBlock
          feature="Microsoft Entra ID Integration"
          benefit="Authenticate your teammates into Canny."
          showBillingLink={false}
        />
      );
    }

    const authURL = this.getAuthURL();
    const { registering } = this.state;
    return (
      <div className="disconnected">
        <a
          className="connectButton"
          href={authURL}
          rel="noreferrer noopener nofollow"
          target="_blank">
          <LazyLoadedImage className="logo" src={AzureLogoIcon} alt="Microsoft Logo" />
          {registering ? <Spinner /> : <div className="label">Connect with Microsoft Entra ID</div>}
        </a>
      </div>
    );
  }

  render() {
    const { hasChangedOffboardSettings, saving, hasChangedMemberAuth } = this.state;

    const { azure, integrations } = this.props.company;
    return (
      <div className="adminAzureSettings">
        <Helmet title="Microsoft Entra ID Integration | Canny" />
        <AdminSettingsHeader
          title="Microsoft Entra ID Integration"
          subheading="Grant your teammates access to Canny by signing in with their company Microsoft Entra ID
          account."
          learnMoreLink="https://help.canny.io/en/articles/3896811-microsoft-entra-id-integration"
        />
        <div className="content">
          {azure ? (
            <>
              {!integrations?.azureAD && azure ? (
                <Alert type={Alert.AlertTypes.Danger} className="alertContainer">
                  <Alert.Heading>
                    Microsoft Entra ID Integration is no longer functioning
                  </Alert.Heading>
                  <Alert.Content>
                    The Microsoft Entra ID integration isn’t included in your plan.
                    Please&nbsp;upgrade to re-enable it.
                  </Alert.Content>
                  <Alert.CTA>
                    <ButtonV2
                      variant="outlined"
                      size="medium"
                      onClick={this.onDisconnect}
                      color="error">
                      Uninstall
                    </ButtonV2>
                  </Alert.CTA>
                </Alert>
              ) : (
                <>
                  {this.renderConnected()}
                  {this.renderForceAuthSection()}
                  {this.renderOffboardSection()}
                  <ButtonV2
                    size="medium"
                    disabled={(!hasChangedOffboardSettings && !hasChangedMemberAuth) || saving}
                    loading={saving}
                    onClick={this.onSave}>
                    Save
                  </ButtonV2>
                </>
              )}
            </>
          ) : (
            this.renderIntegrateButton()
          )}
        </div>
      </div>
    );
  }
}

export default compose(
  connect(null, { reloadCompany }),
  withAccessControl(testEveryPermission(RoutePermissions.integrations.azure), '/admin/settings', {
    forwardRef: true,
  }),
  withContexts(
    {
      company: CompanyContext,
      openModal: OpenModalContext,
      showToast: ShowToastContext,
    },
    {
      forwardRef: true,
    },
  ),
)(AdminAzureSettings);
